import { Box } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import WorkFlowTab from '@/components/WorkFlowTab'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useCanvasSize } from '@/hooks/useCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { useAppSelector } from '@/store'
import {
  useGenerateRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import type { LABColor } from '@/store/appSlice'
import {
  setInternalEffectPosition,
  setInternalEffectRecipeOutput,
  setSelectedInternalEffect,
} from '@/store/pfmCrown'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  hasNonEmptyOutputSingleCanvas,
  saveRecipeGeneric,
} from '@/utils/helper'
import { toLabColor } from '@/utils/matisseV2/toLabColor'

import { imageType } from '../../allInOne/utils/constants'
import InternalEffectPanel from './panel/InternalEffectPanel'

function InternalEffects() {
  const { t } = useTranslation('translation', { keyPrefix: 'internalEffects' })
  const { t: translation } = useTranslation()

  const { containerRef, dimensions } = useCanvasSize()
  const dispatch = useDispatch()
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const location = useLocation()
  const caseId = new URLSearchParams(location.search).get('caseId') || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const [generateRecipe] = useGenerateRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const selectedInternalEffect = useAppSelector(
    (state) => state.pfmCrown.selectedInternalEffect,
  )

  const internalEffectCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.internalEffectCeramicBrand,
  )

  const internalEffectOutput = useAppSelector(
    (state) => state.pfmCrown.internalEffectOutput,
  )
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )

  const onImageClick = (
    _type: string,
    _placement: string,
    imagePos: { x: number; y: number },
    _hiddenCanvasRef: any,
    labColor: LABColor,
  ) => {
    if (
      canvasImage?.id &&
      imageCalibrationStatus?.[canvasImage?.id] ===
        ImageCalibrationStatus.CALIBRATING &&
      _type === imageType.RAW
    ) {
      if (canvasImage?.id) {
        dispatch(
          setImageCalibrationStatus({
            id: canvasImage?.id,
            status: ImageCalibrationStatus.CALIBRATED,
          }),
        )
      }
      return
    }

    if (selectedInternalEffect) {
      if (!internalEffectCeramicBrand) {
        toast.error(t('selectCeramicBrand'))
        return
      }

      dispatch(
        setInternalEffectPosition({
          imageId: canvasImage?.id,
          position: imagePos,
          output: {
            [selectedInternalEffect]: selectedInternalEffect,
          },
        }),
      )
      toast.info(translation('recipeGenerate.inProgress'))
      const targetcolor = toLabColor(labColor)
      generateRecipe({
        caseId,
        recipePath: 'internal-effect',
        data: {
          ceramicbrand: internalEffectCeramicBrand,
          ...(targetcolor ? { targetcolor } : {}),
          effecttype: selectedInternalEffect,
        },
      })
        .unwrap()
        .then((res) => {
          toast.success(t('recipeGenerated'))
          const { generation_id, ...output } = res ?? {}
          dispatch(
            setInternalEffectRecipeOutput({
              imageId: canvasImage?.id,
              key: selectedInternalEffect,
              result: output,
              generation_id,
            }),
          )
        })
        .catch((error) => {
          toast.error(error?.data?.message || t('recipeGenerationFailed'))
        })

      // Clear the selected key immediately
      dispatch(setSelectedInternalEffect(''))
    }
  }
  const onSaveAsImage = () => {
    generateImage(
      combinedRef as React.RefObject<HTMLDivElement>,
      'Internal Effects-Recipe.png',
    )
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: internalEffectOutput[canvasImage?.id],
      workflow: Workflows?.PFM_CROWN,
      mutationFn: saveRecipe,
      closeDialog: () => setSaveRecipedialogOpen(false),
      recipeType: 'internal',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: internalEffectOutput[canvasImage?.id],
      workflow: Workflows?.PFM_CROWN,
      mutationFn: savePdf,
      recipeType: 'internal',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      rightPanel={
        <InternalEffectPanel
          containerRef={containerRef as React.RefObject<HTMLDivElement>}
        />
      }
      disabled={hasNonEmptyOutputSingleCanvas(
        internalEffectOutput,
        canvasImage?.id,
      )}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box
          ref={containerRef}
          display="flex"
          flexGrow={1}
          flexDirection={'column'}
        >
          <WorkFlowTab width={dimensions.width} />
          {!imageEdit ? (
            <>
              <NewKonvaCanvas
                type={canvasImage?.type}
                imageUrl={
                  getDehazedImageUrl(canvasImage) ||
                  canvasImage?.latest_image ||
                  canvasImage?.calibrated_image ||
                  canvasImage?.image
                }
                width={dimensions.width}
                height={dimensions.height}
                onImageClick={onImageClick}
                imageId={canvasImage?.id}
                combinedRef={combinedRef}
                rawWidth={canvasImage?.raw_width}
                rawHeight={canvasImage?.raw_height}
                isActiveCircle={!!selectedInternalEffect}
              />
            </>
          ) : (
            <ImageEdit selectedImage={canvasImage} />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default InternalEffects
